import { format } from 'date-fns';
import { formatterCurrencyTruncated } from '@utilities/format';
import type { Reminders } from '@/components/Note';
import { isCurrencyISO } from '@/constants/currencies';

function formatInTimeZone(date: Date, timeZone: string, formatStr: string) {
  if (!window.Intl || !window.Intl.DateTimeFormat) {
    // Fall back to formatting the date without timezone conversion
    return format(date, formatStr);
  }
  const formatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timeZone,
  } as const;

  /**
   * @todo Pass the locale from the user's preferences or current locale
   */
  const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
  const parts = formatter.formatToParts(date);
  const { year, month, day, hour, minute, second } = Object.fromEntries(
    parts.map(({ type, value }) => [type, value])
  );

  const zonedDate = new Date(
    `${year}-${month}-${day}T${hour}:${minute}:${second}`
  );
  return format(zonedDate, formatStr);
}

export const generateEventBasedReminderMessage = (
  sharePrice: number,
  currencyISO: string,
  variation?: 'exceeds' | 'drops below'
) => {
  const formattedSharePrice = formatterCurrencyTruncated(
    sharePrice,
    currencyISO,
    2
  );

  if (typeof variation === 'undefined') {
    return `We'll email you a reminder when the closing price reaches ${formattedSharePrice}`;
  }

  return `We'll email you a reminder when the closing price ${variation} ${formattedSharePrice}`;
};

export const generateDateBasedReminderMessage = (
  date: Date,
  userTimezone?: string
) => {
  // NOTE: compatibility for https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_resolvedoptions_computed_timezone
  const timezone =
    typeof userTimezone === 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : userTimezone;

  const formattedDate = formatInTimeZone(date, timezone, 'dd MMM yyyy');
  return `We'll email you a reminder on ${formattedDate}`;
};

export const generateReminderMessage = (reminder: Reminders[number]) => {
  if (
    reminder.type === 'date_based' &&
    typeof reminder.deliveryDateUtc === 'string'
  ) {
    const date = new Date(reminder.deliveryDateUtc);
    return generateDateBasedReminderMessage(date, reminder.userTimezone);
  }

  const currencyISO = reminder.reminderTarget?.targetMetadata?.currencyISO;

  if (
    reminder.type === 'event_based' &&
    reminder.reminderTarget &&
    isCurrencyISO(currencyISO)
  ) {
    const sharePrice = reminder.reminderTarget.targetValue;
    return generateEventBasedReminderMessage(sharePrice, currencyISO);
  }
  return reminder.customMessage;
};
